package org.solyton.solawi.bid.application.api


import org.evoleq.ktorx.api.Api
import org.solyton.solawi.bid.application.data.BuildInfo
import org.solyton.solawi.bid.application.data.ReadBackendBuildInfo
import org.solyton.solawi.bid.module.application.data.*
import org.solyton.solawi.bid.module.authentication.data.api.*
import org.solyton.solawi.bid.module.banking.data.SepaPaymentId
import org.solyton.solawi.bid.module.banking.data.api.*
import org.solyton.solawi.bid.module.bid.data.api.*
import org.solyton.solawi.bid.module.distribution.data.api.*
import org.solyton.solawi.bid.module.permission.data.api.*
import org.solyton.solawi.bid.module.shares.data.api.*
import org.solyton.solawi.bid.module.user.data.api.*
import org.solyton.solawi.bid.module.user.data.api.organization.*
import org.solyton.solawi.bid.module.user.data.api.userprofile.*

val solawiApi by lazy {
    // Authentication
    Api{
        get<ReadBackendBuildInfo, BuildInfo>(
            key = ReadBackendBuildInfo::class,
            url = "api/version"
        )

        group("authentication") {
            post<Login, LoggedIn>(
                key = Login::class,
                url = "login"
            )
            post<RefreshToken, LoggedIn>(
                key = RefreshToken::class,
                url = "refresh"
            )
            patch<Logout, Unit>(
                key = Logout::class,
                url = "logout"
            )
            patch<IsLoggedIn, LoggedInAs>(
                key = IsLoggedIn::class,
                url = "is-logged-in"
            )
        }
        // Permissions
        module("permissions") {
            patch<ReadRightRoleContextsOfUser, Contexts>(
                key = ReadRightRoleContextsOfUser::class,
                url = "user/role-right-contexts"
            )
            patch<ReadRightRoleContextsOfUsers, UserToContextsMap>(
                key = ReadRightRoleContextsOfUsers::class,
                url = "users/role-right-contexts"
            )
            patch<ReadParentChildRelationsOfContexts, ParentChildRelationsOfContext>(
                key = ReadParentChildRelationsOfContexts::class,
                url = "contexts/parent-child-relations"
            )
            patch<ReadRightRoleContexts, Contexts>(
                key = ReadRightRoleContexts::class,
                url = "contexts/roles-and-rights"
            )
            put<PutUserRoleContext, UserContext>(
                key = PutUserRoleContext::class,
                url = "user/user-role-context"
            )
            put<PutRoleRightContext, Context>(
                key = PutRoleRightContext::class,
                url = "contexts/role-right-context"
            )
        }
        // Auction
        group("Auctions") {
            post<CreateAuction, Auction>(
                key = CreateAuction::class,
                url = "auction/create"
            )
            get<GetAuctions, List<Auction>>(
                key = GetAuctions::class,
                url = "auction/all"
            )
            delete<DeleteAuctions, List<Auction>>(
                key = DeleteAuctions::class,
                url = "auction/delete"
            )
            patch<UpdateAuctions, List<Auction>>(
                key = UpdateAuctions::class,
                url = "auction/update"
            )
            patch<ConfigureAuction, Auction>(
                key = ConfigureAuction::class,
                url = "auction/configure"
            )
            post<ImportBidders, Auction>(
                key = ImportBidders::class,
                url = "auction/bidder/import"
            )
            post<ImportBiddersFromOrganization, Auction>(
                key = ImportBiddersFromOrganization::class,
                url = "auction/bidder/import-bidders-from-organization"
            )
            delete<DeleteBidders, Auction>(
                key = DeleteBidders::class,
                url = "auction/bidder/delete"
            )
            patch<AcceptRound, AcceptedRound>(
                key = AcceptRound::class,
                url = "auction/accept-round"
            )
            // Round
            get<GetRound, Round>(
                key = GetRound::class,
                url = "round/create---nonsense"
            )
            post<CreateRound, Round>(
                key = CreateRound::class,
                url = "round/create"
            )
            patch<ChangeRoundState, Round>(
                key = ChangeRoundState::class,
                url = "round/change-state"
            )
            patch<ExportBidRound, BidRoundResults>(
                key = ExportBidRound::class,
                url = "round/export-results"
            )
            patch<EvaluateBidRound, BidRoundEvaluation>(
                key = EvaluateBidRound::class,
                url = "round/evaluate"
            )
            patch<PreEvaluateBidRound, BidRoundPreEvaluation>(
                key = PreEvaluateBidRound::class,
                url = "round/pre-evaluate"
            )
            post<CommentOnRound, RoundComments>(
                key = CommentOnRound::class,
                url = "round/add-comment"
            )

            // Auction bid
            post<Bid, BidRound>(
                key = Bid::class,
                url = "bid/send"
            )
            // Search Bidders
            patch<SearchBidderData, BidderMails>(
                key = SearchBidderData::class,
                url = "bidders/search"
            )
            post<AddBidders, Unit>(
                key = AddBidders::class, "bidders/add"
            )

            // Distribution Points
            // ===================
            // takes parameter "provider: UUID"
            get<ReadDistributionPoints, DistributionPoints> (
                key = ReadDistributionPoints::class,
                url = "distribution-points/all"
            )
            get<ReadPersonalDistributionPoints, DistributionPoints> (
                key = ReadPersonalDistributionPoints::class,
                url = "distribution-points/personal"
            )
            post<CreateDistributionPoint, DistributionPoint> (
                key = CreateDistributionPoint::class,
                url = "distribution-points/create"
            )
            patch<UpdateDistributionPoint, DistributionPoint>(
                key = UpdateDistributionPoint::class,
                url = "distribution-points/update"
            )

            // Shares
            // ======
            // ShareType
            post<CreateShareType, ShareType>(
                key = CreateShareType::class,
                url = "shares/types/create"
            )
            get<ReadShareTypes, ShareTypes> (
                key = ReadShareTypes::class,
                url = "shares/types/all"
            )
            patch<UpdateShareType, ShareType>(
                key = UpdateShareType::class,
                url = "shares/types/update"
            )
            // ShareOffer
            post<CreateShareOffer, ShareOffer>(
                key = CreateShareOffer::class,
                url = "shares/offers/create"
            )
            get<ReadShareOffers, ShareOffers> (
                key = ReadShareOffers::class,
                url = "shares/offers/all"
            )
            get<ReadPersonalShareOffers, ShareOffers> (
                key = ReadPersonalShareOffers::class,
                url = "shares/offers/personal"
            )
            patch<UpdateShareOffer, ShareOffer>(
                key = UpdateShareOffer::class,
                url = "shares/offers/update"
            )
            // ShareSubscription
            post<CreateShareSubscription, ShareSubscription>(
                key = CreateShareSubscription::class,
                url = "shares/subscriptions/create"
            )
            get<ReadShareSubscriptions, ShareSubscriptions> (
                key = ReadShareSubscriptions::class,
                url = "shares/subscriptions/all"
            )
            get<ReadPersonalShareSubscriptions, ShareSubscriptions> (
                key = ReadPersonalShareSubscriptions::class,
                url = "shares/subscriptions/personal"
            )
            patch<UpdateShareSubscription, ShareSubscription>(
                key = UpdateShareSubscription::class,
                url = "shares/subscriptions/update"
            )
            post<RolloverShareSubscriptions, ShareSubscriptions>(
                key = RolloverShareSubscriptions::class,
                url = "shares/subscriptions/rollover"
            )
            patch<TransferOwnership, ShareSubscription>(
                key = TransferOwnership::class,
                url = "shares/subscriptions/transfer-ownership"
            )
            post<ImportShareSubscriptions, ShareSubscriptions>(
                key = ImportShareSubscriptions::class,
                url = "shares/subscriptions/import"
            )
            patch<UpdateShareStatus, ShareSubscriptions>(
                key = UpdateShareStatus::class,
                url = "shares/subscriptions/update-status"
            )
            patch<UpdateShareStatuses, ShareSubscriptions>(
                key = UpdateShareStatuses::class,
                url = "shares/subscriptions/update-statuses"
            )

            // History
            get<ReadShareSubscriptionStatusHistory, ShareSubscriptionStatusHistories>(
                key = ReadShareSubscriptionStatusHistory::class,
                url = "shares/subscriptions/history"
            )
        }
        // User Management and Organizations
        group("User management and organizations") {
            post<CreateUser, User>(
                key = CreateUser::class,
                url = "users/create"
            )
            post<UserQuery, Users>(
                key = UserQuery::class,
                url = "users/query"
            )
            get<GetUsers, Users>(
                key = GetUsers::class,
                url = "users/all"
            )
            patch<ChangePassword, User>(
                key = ChangePassword::class,
                url = "users/change-password"
            )
            patch<UpdateUser, User>(
                key = UpdateUser::class,
                url = "users/update"
            )
            patch<UpdateUsername, User>(
                key = UpdateUsername::class,
                url = "users/update-username"
            )

            post<RegisterUser, UserRegistered>(
                key = RegisterUser::class,
                url = "user/register"
            )

            post<SendMailForRegistrationConfirmation, MailForRegistrationConfirmationSent>(
                key = SendMailForRegistrationConfirmation::class,
                url = "user/send-registration-mail"
            )
            post<CreateUserProfile, UserProfile> (
                key = CreateUserProfile::class,
                url = "users/profiles/create"
            )
            post<ImportUserProfiles, UserProfiles> (
                key = ImportUserProfiles::class,
                url = "users/profiles/import"
            )
            patch<UpdateUserProfile, UserProfile> (
                key = UpdateUserProfile::class,
                url = "users/profiles/update"
            )
            // todo:refactor use get with params
            patch<ReadUserProfiles, UserProfiles>(
                key = ReadUserProfiles::class,
                url = "users/profiles/read-by-ids"
            )

            // Organizations
            module("organizations") {
                post<CreateOrganization, Organization>(
                    key = CreateOrganization::class,
                    url = "create"
                )
                post<CreateChildOrganization, Organization>(
                    key = CreateChildOrganization::class,
                    url = "create-child"
                )
                get<ReadOrganizations, Organizations>(
                    key = ReadOrganizations::class,
                    url = "all"
                )
                patch<UpdateOrganization, Organization>(
                    key = UpdateOrganization::class,
                    url = "update"
                )
                delete<DeleteOrganization, Organizations>(
                    key = DeleteOrganization::class,
                    url = "delete"
                )
                post<AddMember, Organization>(
                    key = AddMember::class,
                    url = "members/add"
                )
                patch<UpdateMember, Organization>(
                    key = UpdateMember::class,
                    url = "members/update"
                )
                delete<RemoveMember, Organization>(
                    key = RemoveMember::class,
                    url = "members/remove"
                )
                post<ImportMembers, Organization>(
                    key = ImportMembers::class,
                    url = "members/import"
                )
            }
        }
        // Applications and Modules
        group("applications and modules") {
            module("applications") {
                get<ReadApplications, ApiApplications>(
                    key = ReadApplications::class,
                    url = "all"
                )
                get<ReadPersonalUserApplications, Applications>(
                    key = ReadPersonalUserApplications::class,
                    url = "personal/all"
                )
                get<ReadPersonalApplicationContextRelations, ApplicationContextRelations>(
                    key = ReadPersonalApplicationContextRelations::class,
                    url = "personal/application-context-relations"
                )
                get<ReadPersonalModuleContextRelations, ModuleContextRelations>(
                    key = ReadPersonalModuleContextRelations::class,
                    url = "modules/personal/module-context-relations"
                )
                patch<RegisterForApplications, ApiApplications>(
                    key = RegisterForApplications::class,
                    url = "personal/register"
                )
                patch<StartTrialsOfApplications, ApiApplications>(
                    key = StartTrialsOfApplications::class,
                    url = "personal/trial"
                )
                patch<SubscribeApplications, ApiApplications>(
                    key = SubscribeApplications::class,
                    url = "personal/subscribe"
                )
                patch<RegisterForModules, ApiApplications>(
                    key = RegisterForModules::class,
                    url = "modules/personal/register"
                )
                patch<StartTrialsOfModules, ApiApplications>(
                    key = StartTrialsOfModules::class,
                    url = "modules/personal/trial"
                )
                patch<SubscribeModules, ApiApplications>(
                    key = SubscribeModules::class,
                    url = "modules/personal/subscribe"
                )
                post<ConnectApplicationToOrganization, ApplicationOrganizationRelations>(
                    key = ConnectApplicationToOrganization::class,
                    url = "personal/connect-organization"
                )
                patch<UpdateOrganizationModuleRelations, ApplicationOrganizationRelations>(
                    key = UpdateOrganizationModuleRelations::class,
                    url = "personal/update-organization-module-relations"
                )
                get<ReadApplicationOrganizationContextRelations, ApplicationOrganizationRelations>(
                    key = ReadApplicationOrganizationContextRelations::class,
                    url = "personal/organization-context-relations"
                )


                patch<ReadUserApplications, UserApplications>(
                    key = ReadUserApplications::class,
                    url = "management/users"
                )
                patch<UpdateStandardApplicationContext, Contexts>(
                    key = UpdateStandardApplicationContext::class,
                    url = "management/tech/update-standard-application-context"
                )
                patch<FixApplicationRelatedContexts, Unit>(
                    key = FixApplicationRelatedContexts::class,
                    url = "management/tech/fix-application-related-contexts"
                )
                patch<UpdateStandardModuleContext, Contexts>(
                    key = UpdateStandardModuleContext::class,
                    url = "management/tech/update-standard-module-context"
                )
                patch<FixModuleRelatedContexts, Unit>(
                    key = FixModuleRelatedContexts::class,
                    url = "management/tech/fix-module-related-contexts"
                )
            }
        }

        module("banking") {
            get<ReadLegalEntity, LegalEntity>(
                key = ReadLegalEntity::class,
                url = "legal-entities/personal",
                parameters = setOf("party")
            )
            post<CreateLegalEntity, LegalEntity>(
                key = CreateLegalEntity::class,
                url = "legal-entities/personal/create"
            )
            patch<UpdateLegalEntity, LegalEntity>(
                key = UpdateLegalEntity::class,
                url = "legal-entities/personal/update"
            )
            get<ReadBankAccounts, BankAccounts>(
                key = ReadBankAccounts::class,
                url = "bank-accounts/all",
                parameters = setOf("legal_entity")
            )
            get<ReadPersonalBankAccounts, BankAccounts>(
                key = ReadPersonalBankAccounts::class,
                url = "bank-accounts/personal/all",
                parameters = setOf()
            )
            post<CreateBankAccount, BankAccount>(
                key = CreateBankAccount::class,
                url = "bank-accounts/create"
            )
            post<ImportBankAccounts, BankAccounts>(
                key = ImportBankAccounts::class,
                url = "bank-accounts/import"
            )
            patch<UpdateBankAccount, BankAccount>(
                key = UpdateBankAccount::class,
                url = "bank-accounts/update"
            )
            delete<DeleteBankAccount, Boolean>(
                key = DeleteBankAccount::class,
                url = "bank-accounts/delete"
            )
            // takes parameter "provider: UUID"
            get<ReadFiscalYears, FiscalYears>(
                key = ReadFiscalYears::class,
                url = "fiscal-years/all",
                parameters = setOf("provider")
            )
            post<CreateFiscalYear, FiscalYear>(
                key = CreateFiscalYear::class,
                url = "fiscal-years/create"
            )
            patch<UpdateFiscalYear, FiscalYear>(
                key = UpdateFiscalYear::class,
                url = "fiscal-years/update"
            )

            get<ReadCreditorIdentifierByLegalEntity, CreditorIdentifier>(
                key = ReadCreditorIdentifierByLegalEntity::class,
                url = "creditors/identifiers/by-legal-entity",
                parameters = setOf("legal_entity")
            )
            post<CreateCreditorIdentifier, CreditorIdentifier>(
                key = CreateCreditorIdentifier::class,
                url = "creditors/identifiers/create"
            )
            patch<UpdateCreditorIdentifier, CreditorIdentifier>(
                key = UpdateCreditorIdentifier::class,
                url = "creditors/identifiers/update"
            )

            // Banking /SEPA
            get<ReadSepaCollectionsByLegalEntity, SepaCollections> (
                key = ReadSepaCollectionsByLegalEntity::class,
                url = "sepa/collections/by-legal-entity",
                parameters = setOf("legal_entity")
            )
            post<CreateSepaCollection, SepaCollection>(
                key = CreateSepaCollection::class,
                url = "sepa/collections/create"
            )
            patch<UpdateSepaCollection, SepaCollection>(
                key = UpdateSepaCollection::class,
                url = "sepa/collections/update"
            )

            post<CreateSepaPaymentsForCollection, SepaPayments>(
                key = CreateSepaPaymentsForCollection::class,
                url = "sepa/collections/create-payments"
            )
            post<CreateAdHocSepaPayment, SepaPayment>(
                key = CreateAdHocSepaPayment::class,
                url = "sepa/payments/create-ad-hoc"
            )
            post<CreateSepaPaymentSuccessors, SepaPayments>(
                key = CreateSepaPaymentSuccessors::class,
                url = "sepa/collections/create-payment-successors"
            )
            patch<UpdateSepaPayment, SepaPayment>(
                key = UpdateSepaPayment::class,
                url = "sepa/payments/update"
            )
            patch<UpdateSepaPayments, SepaPayment>(
                key = UpdateSepaPayments::class,
                url = "sepa/payments/update-many"
            )
            delete<DeleteSepaPayment, SepaPaymentId>(
                key = DeleteSepaPayment::class,
                url = "sepa/payments/delete"
            )
            delete<DeleteSepaPayments, SepaPaymentIds>(
                key = DeleteSepaPayments::class,
                url = "sepa/payments/delete-many"
            )
            post<GenerateSepaMessageForCollection, SepaMessageString>(
                key = GenerateSepaMessageForCollection::class,
                url = "sepa/collections/generate-sepa-message"
            )
            get<ReadSepaMessagesByLegalEntityId, SepaMessages>(
                key = ReadSepaMessagesByLegalEntityId::class,
                url = "sepa/messages/by-legal-entity",
                parameters = setOf("legal_entity")
            )
            get<DownloadSepaMessage, SepaMessageString> (
                key = DownloadSepaMessage::class,
                url = "sepa/messages/download",
                parameters = setOf("message_id")
            )
            patch<UpdateSepaMessageStatus, SepaMessage>(
                key = UpdateSepaMessageStatus::class,
                url = "sepa/messages/update-status"
            )
            post<MergeSepaMessages, SepaMessages>(
                key = MergeSepaMessages::class,
                url = "sepa/messages/merge"
            )
            post<CreateSepaMandate, SepaMandate>(
                key = CreateSepaMandate::class,
                url = "sepa/mandates/create"
            )
            get<ReadSepaMandatesByCreditorsLegalEntity, SepaMandates>(
                key = ReadSepaMandatesByCreditorsLegalEntity::class,
                url = "sepa/mandates/by-creditors-legal-entity",
                parameters = setOf("legal_entity")
            )
            get<ReadPersonalSepaMandates, SepaMandates>(
                key = ReadPersonalSepaMandates::class,
                url = "sepa/mandates/personal/all",
                parameters = setOf()
            )
            patch<UpdateSepaMandate, SepaMandate>(
                key = UpdateSepaMandate::class,
                url = "sepa/mandates/update"
            )
            patch<AttachSepaMandateReferenceData, SepaCollection>(
                key = AttachSepaMandateReferenceData::class,
                url = "sepa/mandates/attach-reference-data"
            )
            patch<UpdateSepaPaymentExecutionStatuses, SepaPayments>(
                key = UpdateSepaPaymentExecutionStatuses::class,
                url = "sepa/payments/update-execution-statuses"
            )

            get<ReadPersonalSepaPaymentLinks, SepaPaymentLinks>(
                key = ReadPersonalSepaPaymentLinks::class,
                url = "sepa/payment-links/personal"
            )
            get<ReadSepaPaymentLinksByLegalEntity, SepaPaymentLinks>(
                key = ReadSepaPaymentLinksByLegalEntity::class,
                url = "sepa/payment-links/by-legal-entity",
                parameters = setOf("legal_entity")
            )
        }
    }
}
